import { defaultDemoOrderDetail, demoOrderDetails, demoOrderSearch } from "@/lib/order-demo-data";
import { apiRequest } from "@/lib/auth-api";
import type { ApiEnvelope } from "@/lib/qa-types";
import type {
    CrossBorderDetails,
    CrossBorderUpdateInput,
    ManualOrderCreateData,
    ManualOrderCreateInput,
    OrderDetail,
    OrderSearchData,
    OrderSearchInput,
} from "@/lib/order-types";

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? "/api";

export interface OrderApiResult<T> {
    data: T;
    source: "live" | "demo";
}

async function requestData<T>(path: string, init: RequestInit, fallback: T): Promise<OrderApiResult<T>> {
    try {
        const response = await fetch(`${apiBaseUrl}${path}`, {
            ...init,
            signal: AbortSignal.timeout(1600),
            headers: { Accept: "application/json", "Content-Type": "application/json", ...init.headers },
        });
        if (!response.ok) return { data: fallback, source: "demo" };
        const envelope = (await response.json()) as ApiEnvelope<T>;
        return { data: envelope.data, source: "live" };
    } catch {
        return { data: fallback, source: "demo" };
    }
}

export function searchOrders(input: OrderSearchInput = {}): Promise<OrderApiResult<OrderSearchData>> {
    return requestData(
        "/orders/search",
        {
            method: "POST",
            body: JSON.stringify({
                keyword: input.keyword || undefined,
                statuses: input.statuses ?? [],
                page: input.page ?? 1,
                page_size: input.page_size ?? 20,
            }),
        },
        demoOrderSearch,
    );
}

export function loadOrder(orderId: string): Promise<OrderApiResult<OrderDetail>> {
    return requestData(`/orders/${encodeURIComponent(orderId)}`, { method: "GET" }, demoOrderDetails[orderId] ?? defaultDemoOrderDetail);
}

export function loadCrossBorderDetails(orderId: string): Promise<CrossBorderDetails> {
    return apiRequest(`/orders/${encodeURIComponent(orderId)}/cross-border-details`, { method: "GET" });
}

export function updateCrossBorderDetails(orderId: string, input: CrossBorderUpdateInput, csrfToken: string): Promise<CrossBorderDetails> {
    return apiRequest(`/orders/${encodeURIComponent(orderId)}/cross-border-details/update`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: JSON.stringify(input),
    });
}

export function createManualOrder(input: ManualOrderCreateInput, csrfToken: string): Promise<ManualOrderCreateData> {
    return apiRequest("/orders/create", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
        body: JSON.stringify(input),
    });
}
